import { isAfter, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import type { components } from "../../../apis/gen";
import { formatDateTimeWithDuration } from "../../../utils/date-utils";
import { GameCard } from "../../events-page/components/GameCard";

type Event = components["schemas"]["Event"];
type Session = components["schemas"]["Session"];

type SessionStatus = "upcoming" | "past" | "cancelled" | "unknown";

// Determine session status based on current time
const getSessionStatus = (session: Session): SessionStatus => {
	const now = new Date();
	const sessionEnd = parseISO(session.ended_at);

	if (isAfter(now, sessionEnd)) {
		return "past";
	}
	return "upcoming";
};

interface SummaryTabContentProps {
	event: Event;
}

const SummaryTabContent = ({ event }: SummaryTabContentProps) => {
	const { t } = useTranslation();

	return (
		<div className="mt-5 px-5 lg:px-[calc(50vw-600px)] pb-3 lg:pb-[80px] flex flex-col gap-5">
			<div className="px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
				<div className="flex items-center justify-between">
					<p className="text-xl lg:text-2xl font-medium text-color-Grey900">
						Games
						<span className="lg:hidden mt-2 text-base text-color-Grey700">
							<br />
							You can view each game video and analysis inside.
						</span>
					</p>
				</div>
				{event.sessions && event.sessions.length > 0 ? (
					<div className="mt-4 grid lg:grid-cols-3 gap-3">
						{event.sessions.map((session, idx) => (
							<GameCard
								key={idx}
								session={session}
								idx={idx}
								status={getSessionStatus(session)}
								eventId={event.event_id}
							/>
						))}
					</div>
				) : (
					<p className="mt-4 text-sm text-color-Grey500">
						No games scheduled yet
					</p>
				)}
			</div>
			<div className="flex gap-5">
				<div className="flex-[100] px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
					<p className="text-2xl font-medium text-color-Grey900">Game Info</p>
					<p className="mt-6 text-sm font-medium text-color-Grey500">
						Game Type
					</p>
					<p className="mt-1 text-base text-color-Grey900">
						{t("kiosk.playing.default_game_type")}
					</p>
					<p className="mt-4 text-sm font-medium text-color-Grey500">Date</p>
					<p className="mt-1 text-base text-color-Grey900">
						{formatDateTimeWithDuration(
							event.started_at,
							event.ended_at ?? undefined,
						)}
					</p>
					<p className="mt-4 text-sm font-medium text-color-Grey500">
						Participants
					</p>
					<p className="mt-1 text-base text-color-Grey900">
						{event.participants.length} people
					</p>
					{event.location && (
						<>
							{" "}
							<p className="mt-4 text-sm font-medium text-color-Grey500">
								Location
							</p>
							<p className="mt-1 text-base text-color-Grey900 underline">
								{event.location}
							</p>
							<p className="mt-1 text-base text-color-Grey700">
								106 Taipei City, Daan District, Dunnan Street, Lane 76, No. 28
							</p>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export { SummaryTabContent };
